import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { MagnifyingGlassIcon, FunnelIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { facultyAPI, studentAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AVAILABLE_PERMISSIONS = [
  { value: 'approve_students', label: 'Approve Students', description: 'Can approve/reject student applications' },
  { value: 'manage_clubs', label: 'Manage Clubs', description: 'Can create and manage clubs' },
  { value: 'view_analytics', label: 'View Analytics', description: 'Can view college analytics and reports' },
  { value: 'manage_posts', label: 'Manage Posts', description: 'Can moderate campus feed posts' },
];

const FacultyPage = () => {
  const navigate = useNavigate();
  const { college } = useAuth();
  const AVAILABLE_BRANCHES = college?.branches || [];
  const [filters, setFilters] = useState({
    status: 'all',
    branch: '',
    role: '', // Add role filter
    search: '',
    page: 1,
  });
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [permissions, setPermissions] = useState({
    allowedBranches: [],
    permissions: ['approve_students'],
  });

  const queryClient = useQueryClient();

  // Fetch faculty
  const { data, isLoading, error, refetch } = useQuery(
    ['faculty', filters],
    () => facultyAPI.getFaculty({
      ...filters,
      status: filters.status === 'all' ? undefined : filters.status,
      branch: filters.branch || undefined,
    }),
    {
      keepPreviousData: true,
    }
  );

  // Approve faculty mutation
  const approveMutation = useMutation(
    ({ userId, permissions }) => facultyAPI.approveFaculty(userId, permissions),
    {
      onSuccess: () => {
        toast.success('Faculty approved successfully');
        queryClient.invalidateQueries('faculty');
        queryClient.invalidateQueries('dashboard');
        setShowApproveModal(false);
        setSelectedFaculty(null);
        setPermissions({ allowedBranches: [], permissions: ['approve_students'] });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve faculty');
      },
    }
  );

  // Update faculty permissions mutation
  const updatePermissionsMutation = useMutation(
    ({ userId, permissions }) => facultyAPI.updatePermissions(userId, permissions),
    {
      onSuccess: () => {
        toast.success('Permissions updated successfully');
        queryClient.invalidateQueries('faculty');
        setShowEditModal(false);
        setSelectedFaculty(null);
        setPermissions({ allowedBranches: [], permissions: ['approve_students'] });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update permissions');
      },
    }
  );

  // Reject faculty mutation
  const rejectMutation = useMutation(
    ({ userId, reason }) => facultyAPI.rejectFaculty(userId, reason),
    {
      onSuccess: () => {
        toast.success('Faculty rejected successfully');
        queryClient.invalidateQueries('faculty');
        queryClient.invalidateQueries('dashboard');
        setShowRejectModal(false);
        setSelectedFaculty(null);
        setRejectReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject faculty');
      },
    }
  );

  // Promote to Ambassador mutation
  const promoteAmbassadorMutation = useMutation(studentAPI.promoteToAmbassador, {
    onSuccess: () => {
      toast.success('Student promoted to Ambassador successfully');
      queryClient.invalidateQueries('faculty');
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('dashboard');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to promote to ambassador');
    },
  });

  // Demote from Ambassador mutation
  const demoteAmbassadorMutation = useMutation(studentAPI.demoteAmbassador, {
    onSuccess: () => {
      toast.success('Ambassador demoted to Student successfully');
      queryClient.invalidateQueries('faculty');
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('dashboard');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to demote ambassador');
    },
  });

  const handleApprove = (faculty) => {
    setSelectedFaculty(faculty);
    setPermissions({
      allowedBranches: [faculty.branch], // Default to their own branch
      permissions: ['approve_students'],
    });
    setShowApproveModal(true);
  };

  const handleEdit = (faculty) => {
    setSelectedFaculty(faculty);

    // Map boolean flags back to permission strings
    const currentPermissions = [];
    if (faculty.permissions?.canApproveStudents) currentPermissions.push('approve_students');
    if (faculty.permissions?.canManageClubs) currentPermissions.push('manage_clubs');
    if (faculty.permissions?.canViewAnalytics) currentPermissions.push('view_analytics');
    if (faculty.permissions?.canManagePosts) currentPermissions.push('manage_posts');

    setPermissions({
      allowedBranches: faculty.permissions?.allowedBranches || [],
      permissions: currentPermissions.length > 0 ? currentPermissions : ['approve_students'],
    });
    setShowEditModal(true);
  };

  const handleReject = (faculty) => {
    setSelectedFaculty(faculty);
    setShowRejectModal(true);
  };

  const handleApproveConfirm = () => {
    if (permissions.allowedBranches.length === 0) {
      toast.error('Please select at least one branch');
      return;
    }
    if (permissions.permissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    approveMutation.mutate({
      userId: selectedFaculty._id,
      permissions,
    });
  };

  const handleEditConfirm = () => {
    if (permissions.allowedBranches.length === 0) {
      toast.error('Please select at least one branch');
      return;
    }
    if (permissions.permissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    updatePermissionsMutation.mutate({
      userId: selectedFaculty._id,
      permissions,
    });
  };

  const handleRejectConfirm = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    rejectMutation.mutate({
      userId: selectedFaculty._id,
      reason: rejectReason,
    });
  };

  const handlePromoteToAmbassador = (userId) => {
    if (window.confirm('Are you sure you want to promote this student to Ambassador? They will get special privileges to manage clubs.')) {
      promoteAmbassadorMutation.mutate(userId);
    }
  };

  const handleDemoteAmbassador = (userId) => {
    if (window.confirm('Are you sure you want to demote this ambassador back to Student? They will lose ambassador privileges.')) {
      demoteAmbassadorMutation.mutate(userId);
    }
  };

  const handleBranchToggle = (branch) => {
    setPermissions(prev => ({
      ...prev,
      allowedBranches: prev.allowedBranches.includes(branch)
        ? prev.allowedBranches.filter(b => b !== branch)
        : [...prev.allowedBranches, branch],
    }));
  };

  const handlePermissionToggle = (permission) => {
    setPermissions(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  if (isLoading) return <LoadingSpinner text="Loading faculty..." />;

  if (!college || !college.branches || college.branches.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-amber-600 mb-4">No branches found for your college</p>
        <p className="text-sm text-gray-500 mb-4">
          Please ensure your college has branches configured in the system.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error loading faculty</p>
        <button onClick={() => refetch()} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  const { faculty = [], pagination = {} } = data?.data?.data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faculty & Ambassador Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage faculty & ambassador registrations, approvals, and permissions
          </p>
        </div>
      </div>

      {/* Available Branches Info */}
      <div className="card p-4 bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          Available Branches in {college.name}
        </h3>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_BRANCHES.map((branch) => (
            <span
              key={branch}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
            >
              {branch}
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              className="input pl-10"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          {/* Role Filter */}
          <div>
            <select
              className="input"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="Faculty">Faculty</option>
              <option value="Ambassador">Ambassador</option>
              <option value="Student">Approved Students</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              className="input pl-10"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Branch Filter */}
          <div>
            <select
              className="input"
              value={filters.branch}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
            >
              <option value="">All Branches</option>
              {AVAILABLE_BRANCHES.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>

          {/* Reset Filters */}
          <button
            onClick={() => setFilters({ status: 'all', branch: '', role: '', search: '', page: 1 })}
            className="btn-secondary"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Faculty List */}
      <div className="card">
        {faculty.length === 0 ? (
          <div className="text-center py-12">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No faculty or ambassadors found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No faculty or ambassadors match the current filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name & Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {faculty.map((member) => (
                  <tr key={member._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-green-800">
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div
                            className="text-sm font-medium text-gray-900 hover:text-primary-600 cursor-pointer"
                            onClick={() => navigate(`/users/${member._id}`)}
                          >
                            {member.name}
                          </div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                            {member.role || 'Faculty'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="badge badge-green">{member.branch}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`badge ${
                          member.isVerified
                            ? 'badge-green'
                            : member.isRejected
                            ? 'badge-red'
                            : 'badge-yellow'
                        }`}
                      >
                        {member.isVerified
                          ? 'Approved'
                          : member.isRejected
                          ? 'Rejected'
                          : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {member.isVerified && member.permissions ? (
                        <div className="space-y-1">
                          <div className="text-xs text-gray-500">
                            Branches: {member.permissions.allowedBranches?.join(', ') || 'None'}
                          </div>
                          <div className="text-xs text-gray-500">
                            Perms: {[
                              member.permissions.canApproveStudents && 'Approve',
                              member.permissions.canManageClubs && 'Clubs',
                              member.permissions.canViewAnalytics && 'Analytics',
                              member.permissions.canManagePosts && 'Posts'
                            ].filter(Boolean).join(', ') || 'None'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(member.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {!member.isVerified && !member.isRejected && (
                        <>
                          <button
                            onClick={() => handleApprove(member)}
                            disabled={approveMutation.isLoading}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(member)}
                            disabled={rejectMutation.isLoading}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {member.isVerified && (
                        <>
                          <button
                            onClick={() => handleEdit(member)}
                            disabled={updatePermissionsMutation.isLoading}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                          >
                            Edit Permissions
                          </button>
                          {member.role === 'Student' && (
                            <button
                              onClick={() => handlePromoteToAmbassador(member._id)}
                              disabled={promoteAmbassadorMutation.isLoading}
                              className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                            >
                              ↑ Promote to Ambassador
                            </button>
                          )}
                          {member.role === 'Ambassador' && (
                            <button
                              onClick={() => handleDemoteAmbassador(member._id)}
                              disabled={demoteAmbassadorMutation.isLoading}
                              className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                            >
                              ↓ Demote to Student
                            </button>
                          )}
                        </>
                      )}
                      {member.isRejected && member.rejectionReason && (
                        <span
                          className="text-gray-600 cursor-help"
                          title={member.rejectionReason}
                        >
                          View Reason
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(pagination.current - 1) * 20 + 1} to{' '}
              {Math.min(pagination.current * 20, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={!pagination.hasPrev}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-600">
                Page {pagination.current} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={!pagination.hasNext}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Approve {selectedFaculty?.role || 'Faculty'} & Assign Permissions
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure permissions for {selectedFaculty?.name} ({selectedFaculty?.role || 'Faculty'})
              </p>

              {/* Branch Permissions */}
              <div className="mb-6">
                <label className="label">Allowed Branches *</label>
                <p className="text-sm text-gray-600 mb-3">
                  Select branches this {(selectedFaculty?.role || 'Faculty').toLowerCase()} can approve students from
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_BRANCHES.map((branch) => (
                    <label
                      key={branch}
                      className="flex items-center space-x-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={permissions.allowedBranches.includes(branch)}
                        onChange={() => handleBranchToggle(branch)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{branch}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* System Permissions */}
              <div className="mb-6">
                <label className="label">System Permissions *</label>
                <p className="text-sm text-gray-600 mb-3">
                  Select what this {(selectedFaculty?.role || 'Faculty').toLowerCase()} can do in the system
                </p>
                <div className="space-y-2">
                  {AVAILABLE_PERMISSIONS.map((perm) => (
                    <label
                      key={perm.value}
                      className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={permissions.permissions.includes(perm.value)}
                        onChange={() => handlePermissionToggle(perm.value)}
                        className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{perm.label}</span>
                        <p className="text-xs text-gray-600">{perm.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedFaculty(null);
                    setPermissions({ allowedBranches: [], permissions: ['approve_students'] });
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveConfirm}
                  disabled={approveMutation.isLoading}
                  className="btn-primary"
                >
                  {approveMutation.isLoading ? 'Approving...' : `Approve ${selectedFaculty?.role || 'Faculty'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reject {selectedFaculty?.role || 'Faculty'} Application
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting {selectedFaculty?.name}'s ({selectedFaculty?.role || 'Faculty'}) application.
              </p>
              <textarea
                className="input h-24 mb-4"
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedFaculty(null);
                    setRejectReason('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  disabled={rejectMutation.isLoading}
                  className="btn-danger"
                >
                  {rejectMutation.isLoading ? 'Rejecting...' : `Reject ${selectedFaculty?.role || 'Faculty'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Permissions Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Permissions for {selectedFaculty?.name}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Update permissions and allowed branches for {selectedFaculty?.name} ({selectedFaculty?.role || 'Faculty'})
              </p>

              {/* Branch Permissions */}
              <div className="mb-6">
                <label className="label">Allowed Branches *</label>
                <p className="text-sm text-gray-600 mb-3">
                  Select branches this {(selectedFaculty?.role || 'Faculty').toLowerCase()} can approve students from
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_BRANCHES.map((branch) => (
                    <label
                      key={branch}
                      className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={permissions.allowedBranches.includes(branch)}
                        onChange={() => handleBranchToggle(branch)}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">{branch}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* System Permissions */}
              <div className="mb-6">
                <label className="label">System Permissions *</label>
                <p className="text-sm text-gray-600 mb-3">
                  Select what this {(selectedFaculty?.role || 'Faculty').toLowerCase()} can do in the system
                </p>
                <div className="space-y-2">
                  {AVAILABLE_PERMISSIONS.map((perm) => (
                    <label
                      key={perm.value}
                      className="flex items-start space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={permissions.permissions.includes(perm.value)}
                        onChange={() => handlePermissionToggle(perm.value)}
                        className="mt-1 rounded text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{perm.label}</div>
                        <div className="text-xs text-gray-500">{perm.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedFaculty(null);
                    setPermissions({ allowedBranches: [], permissions: ['approve_students'] });
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditConfirm}
                  disabled={updatePermissionsMutation.isLoading}
                  className="btn-primary"
                >
                  {updatePermissionsMutation.isLoading ? 'Updating...' : 'Update Permissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyPage;